// Per-field validation for the entry forms (DESIGN-SPEC §6.2): each field gets
// its own message on submit, a field's message clears as soon as that field
// changes, and only server outcomes (wrong credentials, rate limit, outage)
// stay form-level. Codes are the server's where one exists, so a server answer
// lands under the same field as the matching client check.
//
// Email format uses the browser's constraint validation (type=email), not a
// pattern of our own; the server's check is the authority and answers
// `email_invalid` for anything the browser let through.

/** The server's password floor (src/http-auth.mjs validSignupCredentials). */
export const PASSWORD_MIN_LENGTH = 15;
/** The server's company-name ceiling (src/organizations/signup.mjs). */
export const COMPANY_NAME_MAX_LENGTH = 120;

export type EntryFieldName = 'company' | 'email' | 'password' | 'confirm';
export type FieldErrors = Partial<Record<EntryFieldName, string>>;

export interface EmailState {
  value: string;
  /** True when the browser reports a type mismatch for the email input. */
  malformed: boolean;
}

const characters = (value: string) => [...value].length;

function emailError({ value, malformed }: EmailState): string | undefined {
  if (!value.trim()) return 'email_required';
  if (malformed) return 'email_invalid';
  return undefined;
}

// A new password's message replaces its "At least 15 characters" helper in the
// same slot, so an empty one states the rule too rather than "Enter a password".
function newPasswordError(password: string): string | undefined {
  return characters(password) < PASSWORD_MIN_LENGTH ? 'password_too_short' : undefined;
}

function compact(errors: FieldErrors): FieldErrors {
  return Object.fromEntries(
    Object.entries(errors).filter(([, code]) => Boolean(code)),
  ) as FieldErrors;
}

/** Sign-up: company (only when creating one), email (unless invitation-bound), password. */
export function signupErrors({
  company,
  email,
  password,
}: {
  company: string | null;
  email: EmailState | null;
  password: string;
}): FieldErrors {
  return compact({
    company:
      company === null
        ? undefined
        : !company.trim()
          ? 'company_name_required'
          : characters(company.trim()) > COMPANY_NAME_MAX_LENGTH
            ? 'invalid_company_details'
            : undefined,
    email: email ? emailError(email) : undefined,
    password: newPasswordError(password),
  });
}

export function loginErrors({
  email,
  password,
}: {
  email: EmailState | null;
  password: string;
}): FieldErrors {
  return compact({
    email: email ? emailError(email) : undefined,
    password: password ? undefined : 'password_required',
  });
}

export function emailOnlyErrors(email: EmailState): FieldErrors {
  return compact({ email: emailError(email) });
}

export function resetErrors({
  password,
  confirmation,
}: {
  password: string;
  confirmation: string;
}): FieldErrors {
  const passwordCode = newPasswordError(password);
  if (passwordCode) return { password: passwordCode };
  if (!confirmation) return { confirm: 'password_required' };
  return confirmation === password ? {} : { confirm: 'password_mismatch' };
}

const FIELD_FOR_SERVER_CODE: Readonly<Record<string, EntryFieldName>> = {
  company_name_required: 'company',
  invalid_company_details: 'company',
  work_email_required: 'email',
  email_invalid: 'email',
  email_taken: 'email',
  password_too_short: 'password',
  password_too_long: 'password',
  password_compromised: 'password',
};

/** The field a server error belongs under, or null for a form-level outcome. */
export function fieldForServerCode(code: string): EntryFieldName | null {
  return Object.hasOwn(FIELD_FOR_SERVER_CODE, code) ? FIELD_FOR_SERVER_CODE[code] : null;
}

export const hasErrors = (errors: FieldErrors) => Object.keys(errors).length > 0;

/** Clear one field's message when that field changes; keep the others. */
export function clearField(errors: FieldErrors, field: EntryFieldName): FieldErrors {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}
